/**
 * Step 06 — the caption face (spec §2.2).
 *
 * One decision: the face the captions are set in. The speaker-name face is
 * asked on the screen where it is visible; numbers keep the display face
 * silently (a render rule, not a question).
 *
 * Every row is rendered IN its own face. A face that has not finished
 * downloading shows a shimmering placeholder rather than a system-sans flash —
 * on the screen where you choose a font, a wrong face on screen is a lie.
 */
import { useEffect, useRef, useState, type Dispatch } from "react";
import { Type, Upload } from "lucide-react";
import type { FontFamilyEntry } from "../../../api";
import {
  defaultFontsForLanguage,
  effectiveFontNames,
  FOUNDING_FONT_NAMES,
  type BrandSetupAction,
  type BrandSetupState,
} from "../../../lib/brandSetup/state";
import { MONO_LABEL, REASON } from "../ui";

export function FontStep(props: {
  state: BrandSetupState;
  dispatch: Dispatch<BrandSetupAction>;
  families: FontFamilyEntry[];
  /** Upload / library failures, as a sentence. */
  error: string | null;
  /** The uploaded font's bytes didn't survive a refresh (spec §2.6). */
  note: string | null;
  onUploadFont: (file: File) => void;
}): React.JSX.Element {
  const { state, dispatch, families, error, note } = props;
  const fileRef = useRef<HTMLInputElement | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);
  const selectedRef = useRef<HTMLButtonElement | null>(null);
  const uploaded = state.fonts.uploaded?.family;
  const seed = defaultFontsForLanguage(state.language);
  const current = effectiveFontNames(state).body;
  const chip = state.chips.find((c) => c.field === "fontRoles.caption");
  const fromReel = chip && state.accepted[chip.field] !== false;
  const fontClass = classOf(chip?.value);

  // The list the operator chooses from: their own upload first, then the
  // library, and the founding face when the language falls back to it (RTL
  // leaves the role blank on purpose — it still renders in something, and
  // that something has to be nameable and clickable).
  const names: string[] = [];
  if (uploaded) names.push(uploaded);
  for (const f of families) if (f.kind !== "uploaded" && !names.includes(f.family)) names.push(f.family);
  if (!seed.body && !names.includes(FOUNDING_FONT_NAMES.body)) names.unshift(FOUNDING_FONT_NAMES.body);
  // Fonts other brands uploaded (spec 2026-09-16 round 2 §2.5) — their own
  // section under the library. Picking one is a plain `fonts.body`; create-
  // time role resolution copies that brand's faces onto the new one. A pick
  // stays in its section rather than jumping to the top of the list.
  const others: string[] = [];
  for (const f of families) {
    if (f.kind === "uploaded" && !names.includes(f.family) && !others.includes(f.family)) others.push(f.family);
  }
  if (!names.includes(current) && !others.includes(current)) names.unshift(current);

  const loaded = useFontsLoaded([...names, ...others]);

  // Eighteen faces do not fit; the one you are ON must still be the one you
  // see on arrival. Scrolls the LIST, never the page — and only when the list
  // itself changes (a click must not move the row out from under the cursor).
  useEffect(() => {
    const list = listRef.current;
    const row = selectedRef.current;
    if (!list || !row) return;
    const top = row.offsetTop - list.offsetTop;
    if (top < list.scrollTop || top + row.offsetHeight > list.scrollTop + list.clientHeight) {
      list.scrollTop = Math.max(0, top - 8);
    }
  }, [names.length, others.length]);

  const row = (family: string, i: number): React.JSX.Element => {
    const on = family === current;
    const ready = loaded.has(family);
    return (
      <button
        key={family}
        ref={on ? selectedRef : undefined}
        type="button"
        role="radio"
        aria-checked={on}
        {...(i === 0 ? { "data-autofocus": true } : {})}
        onClick={() => dispatch({ type: "fonts", fonts: { body: family === FOUNDING_FONT_NAMES.body && !seed.body ? "" : family } })}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          width: "100%",
          padding: "12px 16px",
          borderRadius: 14,
          cursor: "pointer",
          textAlign: "left",
          border: on ? "1.5px solid var(--ql-yellow)" : "1px solid rgba(255,255,255,.08)",
          background: on ? "rgba(255,213,0,.06)" : "rgba(255,255,255,.02)",
          transition: "all .15s var(--ql-ease)",
        }}
      >
        <span
          aria-hidden
          style={{
            width: 15,
            height: 15,
            borderRadius: 999,
            flexShrink: 0,
            border: on ? "4.5px solid var(--ql-yellow)" : "1.5px solid rgba(255,255,255,.22)",
          }}
        />
        <span style={{ flex: 1, minWidth: 0, position: "relative" }}>
          <span
            dir={state.language.direction}
            style={{
              display: "block",
              fontFamily: `"${family}", var(--ql-font-sans)`,
              fontSize: 22,
              fontWeight: 700,
              lineHeight: 1.25,
              color: "var(--ql-fg)",
              visibility: ready ? "visible" : "hidden",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {family}
          </span>
          {!ready && (
            <span
              aria-hidden
              style={{
                position: "absolute",
                inset: "6px 40% 6px 0",
                borderRadius: 6,
                background:
                  "linear-gradient(90deg, rgba(255,255,255,.05), rgba(255,255,255,.12), rgba(255,255,255,.05))",
                backgroundSize: "200% 100%",
                animation: "ql-shimmer 1.1s linear infinite",
              }}
            />
          )}
        </span>
      </button>
    );
  };

  return (
    <div style={{ display: "grid", gap: 14 }}>
      {fromReel && (
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <span
            style={{
              ...MONO_LABEL,
              color: "var(--ql-yellow)",
              padding: "4px 9px",
              borderRadius: 999,
              border: "1px solid rgba(255,213,0,.35)",
              background: "rgba(255,213,0,.06)",
            }}
          >
            Closest to your reel — {fontClass}
          </span>
          <button
            type="button"
            onClick={() => dispatch({ type: "resetChip", field: "fontRoles.caption" })}
            style={{
              border: "none",
              background: "transparent",
              color: "var(--ql-fg-muted)",
              fontSize: 13,
              fontFamily: "inherit",
              textDecoration: "underline",
              cursor: "pointer",
              padding: 0,
            }}
          >
            Use ours instead
          </button>
        </div>
      )}

      <div
        ref={listRef}
        role="radiogroup"
        aria-label="Caption font"
        style={{ display: "grid", gap: 6, maxHeight: 360, overflowY: "auto", paddingRight: 4 }}
      >
        {names.map((family, i) => row(family, i))}
        {others.length > 0 && (
          <>
            <div style={{ ...MONO_LABEL, padding: "10px 4px 2px" }}>From your other brands</div>
            {others.map((family, i) => row(family, names.length + i))}
          </>
        )}
      </div>
      <style>{`@keyframes ql-shimmer { from { background-position: 200% 0 } to { background-position: -200% 0 } }
@media (prefers-reduced-motion: reduce) { [style*="ql-shimmer"] { animation: none !important } }`}</style>

      <div>
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            padding: "11px 18px",
            borderRadius: 12,
            border: "1px dashed rgba(255,255,255,.16)",
            background: "transparent",
            color: "var(--ql-fg-muted)",
            fontSize: 13,
            fontWeight: 600,
            fontFamily: "inherit",
            cursor: "pointer",
          }}
        >
          {uploaded ? <Type size={15} /> : <Upload size={15} />}
          {uploaded ? `${uploaded} — upload another` : "Upload a font (.ttf / .otf)"}
        </button>
        <input
          ref={fileRef}
          type="file"
          accept=".ttf,.otf"
          style={{ display: "none" }}
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) props.onUploadFont(f);
            if (fileRef.current) fileRef.current.value = "";
          }}
        />
        {note && <div style={{ fontSize: 13, color: "var(--ql-fg-muted)", lineHeight: 1.55, marginTop: 8 }}>{note}</div>}
        {error && <div style={{ ...REASON, marginTop: 8 }}>{error}</div>}
      </div>
    </div>
  );
}

/** The family of shapes the study matched, in the operator's words. The chip
 *  carries a family NAME ("Poppins"); when the study also says what kind of
 *  face it is we use that word, and otherwise the honest default — the study
 *  only ever proposes a bold sans for captions. */
function classOf(value: unknown): string {
  const text = String(value ?? "").toLowerCase();
  for (const word of ["serif", "script", "slab", "mono", "display", "condensed"])
    if (text.includes(word)) return `bold ${word}`;
  return "bold sans";
}

/**
 * Which of `families` the browser can actually draw with right now. Each face
 * is asked for ONCE (`document.fonts.load`), and the set only grows.
 *
 * No cancellation flag on purpose: a load asked for by one render must still
 * be allowed to mark the face when it lands, even if the list has changed
 * since. Cancelling it left rows in the skeleton forever — the face WAS
 * loaded, the row just never heard about it (seen in the browser walk).
 */
function useFontsLoaded(families: string[]): Set<string> {
  const [loaded, setLoaded] = useState<Set<string>>(new Set());
  const asked = useRef<Set<string>>(new Set());
  const key = families.join("|");

  useEffect(() => {
    const fonts = typeof document !== "undefined" ? document.fonts : undefined;
    const mark = (family: string): void =>
      setLoaded((prev) => (prev.has(family) ? prev : new Set(prev).add(family)));
    if (!fonts) {
      // No Font Loading API: show every row rather than an eternal skeleton.
      setLoaded(new Set(families));
      return;
    }
    for (const family of families) {
      const spec = `700 22px "${family}"`;
      let ready = false;
      try {
        ready = fonts.check(spec);
      } catch {
        ready = true;
      }
      if (ready) {
        mark(family);
        continue;
      }
      if (asked.current.has(family)) continue;
      asked.current.add(family);
      fonts
        .load(spec)
        .then(() => mark(family))
        // A face that will not load is still a choice we can name — showing
        // the row in the fallback beats a skeleton that never resolves.
        .catch(() => mark(family));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return loaded;
}
